import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useReveal — observes the attached element via a CALLBACK ref so the observer
 * is set up whenever the node mounts (handles late conditional-render mounts).
 *
 * Returns:
 *   • ref         — callback ref. Attach as <section ref={ref}>.
 *   • visible     — true after the element has entered the viewport once
 *                   (sticky by default so content never disappears).
 *   • revealKey   — increments every time the element re-enters the viewport,
 *                   useful for restarting CSS animations via key={revealKey}.
 *
 * Options:
 *   • visibleOnce (default true) — set false to make `visible` toggle off
 *     when the element leaves the viewport.
 */
export default function useReveal(options = {}) {
  const { visibleOnce = true, threshold = 0, rootMargin = "0px 0px -10% 0px" } = options;
  const [visible, setVisible] = useState(false);
  const [revealKey, setRevealKey] = useState(0);
  const obsRef = useRef(null);
  const nodeRef = useRef(null);

  const ref = useCallback(
    (node) => {
      if (obsRef.current) {
        obsRef.current.disconnect();
        obsRef.current = null;
      }
      nodeRef.current = node;
      if (!node) return;
      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              setVisible(true);
              setRevealKey((k) => k + 1);
            } else if (!visibleOnce) {
              setVisible(false);
            }
          });
        },
        { threshold, rootMargin }
      );
      obs.observe(node);
      obsRef.current = obs;
    },
    [visibleOnce, threshold, rootMargin]
  );

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (obsRef.current) obsRef.current.disconnect();
    };
  }, []);

  return { ref, visible, revealKey };
}

/**
 * useCountUp — animates a number from 0 to `target` over `duration` ms when
 * `trigger` changes to a new truthy value. Pair with revealKey for re-runs.
 */
export function useCountUp(target, trigger, duration = 1400) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!trigger) { setVal(0); return; }
    const numeric = parseInt(String(target).replace(/[^\d]/g, ""), 10);
    if (!Number.isFinite(numeric)) { setVal(target); return; }
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(Math.round(eased * numeric));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [trigger, target, duration]);
  const suffix = String(target).replace(/[\d,\s]/g, "");
  return `${val}${suffix}`;
}
